import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const LoginModal = ({ setLoggedInUser }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('patient'); 
  const [error, setError] = useState('');

  // Helper to clear error when user types
  const handleInput = (setter, value) => {
    setter(value);
    if (error) setError('');
  };

  // FIX 1: Clear data when switching tabs (Patient/Doctor/Hospital)
  const switchRole = (newRole) => {
    setRole(newRole);
    setEmail('');
    setPassword('');
    setError('');
  };

  // FIX 2: Reset form when closing the modal
  const handleClose = () => {
    setEmail('');
    setPassword('');
    setError('');
    setRole('patient');
  };

  const handleLogin = (e) => {
    e.preventDefault();

    const cleanEmail = email.trim();
    const cleanPassword = password.trim();

    // Regex Validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (cleanPassword.length < 1) {
      setError("Please enter your password.");
      return;
    }

    const credentials = { email: cleanEmail, password: cleanPassword, role };

    axios.post(process.env.REACT_APP_API_BASE_URL + "/users/login", credentials)
      .then(res => {
        if (res.data.status === 'ok') {
          // 1. Save to Storage
          localStorage.setItem('DDoctorUser', JSON.stringify(res.data.user));
          setLoggedInUser(res.data.user);
          
          // 2. Close Modal
          const closeBtn = document.getElementById('loginModalCloseBtn');
          if(closeBtn) closeBtn.click();

          // 3. Reset Form (Manually call this to be safe)
          handleClose();

          // 4. Redirect Logic
          if (res.data.user.role === 'hospital') {
            navigate('/hospital-dashboard');
          } else if (res.data.user.role === 'doctor') {
            navigate('/doctor-dashboard');
          } else {
            navigate('/profile');
          }
        } else {
            // Handle valid connection but "failed" status (rare)
            setError(res.data.message || "Login failed.");
        }
      })
      .catch(err => {
        console.error(err);
        if (err.response) {
            // Specific Error Handling
            if (err.response.status === 404) {
                setError("This email is not registered. Please Sign Up first.");
            } else if (err.response.status === 400 || err.response.status === 401) {
                setError("Incorrect Password. Please try again.");
            } else {
                setError(err.response.data.message || "Server Error. Please try again later.");
            }
        } else {
            setError("Network Error. Please check your connection.");
        }
      });
  };

  const getButtonStyle = (btnRole) => {
    return {
      cursor: 'pointer',
      border: '1px solid #000', 
      backgroundColor: role === btnRole ? 'rgb(255, 193, 7)' : 'white', 
      color: role === btnRole ? 'white' : 'black',                  
      fontWeight: 'bold'
    };
  };

  return (
    <div className="modal fade login_style" id="loginModal" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog">
        <div className="modal-content">
          <form onSubmit={handleLogin}>

            {/* Header */}
            <div className="modal-header border-0">
              {error && (
                  <div className="alert alert-danger d-flex align-items-center p-2 mb-3 w-100" role="alert">
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      <div className="small fw-bold">{error}</div>
                  </div>
              )}
              {/* Only show Title if no error, or show below? Keeping logic simple as per request */}
              {!error && (
                <h5 className="modal-title d-flex align-items-center fw-bold">
                    <i className="bi bi-person-circle fs-3 me-2"></i> Login
                </h5>
              )}
              
              {/* FIX: Added onClick={handleClose} to reset form when X is clicked */}
              <button 
                type="button" 
                id="loginModalCloseBtn" 
                className="btn-close shadow-none" 
                data-bs-dismiss="modal" 
                aria-label="Close"
                onClick={handleClose}
              ></button>
            </div>

            <div className="modal-body pt-0">
              
              {/* Register Partner Hospital Link (Kept at top as per your code) */}
              <div className="text-center mt-3 pt-3 border-top">
                <small className="text-muted">Are you a Hospital?</small>
                <br />
                <button
                  type='button'
                  className="btn btn-sm btn-link text-decoration-none fw-bold"
                  data-bs-toggle="modal"
                  data-bs-target="#hospitalRegisterModal"
                  onClick={() => {
                      document.getElementById('loginModalCloseBtn').click();
                      handleClose(); // Clear form when switching modals
                  }}
                >
                  Register as Partner Hospital
                </button>
              </div>

              {/* --- Role Selection Tabs --- */}
              <div className="d-flex gap-2 mb-4 mt-3">
                <button
                  type="button"
                  className="btn flex-fill shadow-none"
                  onClick={() => switchRole('patient')}
                  style={getButtonStyle('patient')}
                >
                  Patient
                </button>
                <button
                  type="button"
                  className="btn flex-fill shadow-none"
                  onClick={() => switchRole('doctor')}
                  style={getButtonStyle('doctor')}
                >
                  Doctor
                </button>
                <button
                  type="button"
                  className="btn flex-fill shadow-none"
                  onClick={() => switchRole('hospital')}
                  style={getButtonStyle('hospital')}
                >
                  Hospital
                </button>
              </div>

              {/* Inputs */}
              <div className="mb-3">
                <label className="form-label fw-bold">Email address</label>
                <input
                  type="email"
                  className="form-control shadow-none"
                  value={email}
                  onChange={(e) => handleInput(setEmail, e.target.value)}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="form-label fw-bold">Password</label>
                <input
                  type="password"
                  className="form-control shadow-none"
                  value={password}
                  onChange={(e) => handleInput(setPassword, e.target.value)}
                  required
                />
              </div>

              {/* Login Button */}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <button
                  type="submit"
                  className="btn btn-dark shadow-none w-100 py-2 fw-bold"
                  style={{ backgroundColor: 'rgb(40, 37, 97)' }}
                >
                  LOGIN AS {role.toUpperCase()}
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;